import React, { useState, useEffect, useCallback } from 'react';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Building2, Loader2, Search, Wallet, ArrowDownCircle, ArrowUpCircle,
  ArrowLeftRight, ChevronLeft, ChevronRight, Receipt, Download
} from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => `NGN ${Math.round(Number(n || 0)).toLocaleString()}`;

export default function CompanyTransactionsPage() {
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [exporting, setExporting] = useState(false);

  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try { setCompanies(await superAdminAPI.getCompanies()); }
      catch { toast.error('Failed to load companies'); }
      finally { setLoadingCompanies(false); }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!selectedCompanyId) return;
    setLoading(true);
    try {
      const res = await superAdminAPI.getCompanyTransactions(selectedCompanyId, {
        tab, search, page, page_size: pageSize,
      });
      setData(res);
    } catch { toast.error('Failed to load transactions'); }
    finally { setLoading(false); }
  }, [selectedCompanyId, tab, search, page]);

  useEffect(() => { load(); }, [load]);

  const handleExport = async () => {
    if (!selectedCompanyId) return;
    setExporting(true);
    try {
      const res = await superAdminAPI.exportCompanyTransactions(selectedCompanyId, { tab, search });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = res.headers?.['content-disposition']?.split('filename="')[1]?.replace('"', '') || `transactions_${tab}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  const tabs = [
    { id: 'all', label: 'All Transactions', icon: ArrowLeftRight },
    { id: 'disbursements', label: 'Disbursements', icon: ArrowUpCircle },
    { id: 'inflows', label: 'Inflows', icon: ArrowDownCircle },
  ];

  if (loadingCompanies) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  return (
    <div className="space-y-5" data-testid="company-transactions-page">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Disbursements & Transactions</h1>
        <p className="text-sm text-gray-500">View all company wallet transactions, disbursements, and inflows</p>
      </div>

      {/* Company Selector */}
      <Card className="border-gray-200 shadow-sm">
        <CardContent className="p-4">
          <label className="text-sm font-medium mb-2 block">Select Company</label>
          <Select value={selectedCompanyId || 'none'} onValueChange={v => { setSelectedCompanyId(v === 'none' ? '' : v); setPage(0); setData(null); }}>
            <SelectTrigger className="h-10 border-gray-200" data-testid="tx-company-selector">
              <Building2 className="w-4 h-4 mr-2 text-gray-400" />
              <SelectValue placeholder="Choose a company" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Choose a company...</SelectItem>
              {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {selectedCompanyId && data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card className="border-gray-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className="w-4 h-4 text-blue-500" />
                  <span className="text-[10px] text-gray-400 uppercase">Balance</span>
                </div>
                <p className="text-sm font-bold" data-testid="tx-balance">{fmt(data.wallet_balance)}</p>
              </CardContent>
            </Card>
            <Card className="border-gray-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowDownCircle className="w-4 h-4 text-emerald-500" />
                  <span className="text-[10px] text-gray-400 uppercase">Total Inflows</span>
                </div>
                <p className="text-sm font-bold text-emerald-600" data-testid="tx-inflows">{fmt(data.total_inflows)}</p>
              </CardContent>
            </Card>
            <Card className="border-gray-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowUpCircle className="w-4 h-4 text-pink-500" />
                  <span className="text-[10px] text-gray-400 uppercase">Total Disbursed</span>
                </div>
                <p className="text-sm font-bold text-pink-600" data-testid="tx-disbursed">{fmt(data.total_disbursed)}</p>
              </CardContent>
            </Card>
            <Card className="border-gray-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Receipt className="w-4 h-4 text-gray-500" />
                  <span className="text-[10px] text-gray-400 uppercase">Showing</span>
                </div>
                <p className="text-sm font-bold">{data.total} transactions</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200">
            {tabs.map(t => (
              <button key={t.id} onClick={() => { setTab(t.id); setPage(0); }}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  tab === t.id ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`} data-testid={`tx-tab-${t.id}`}>
                <t.icon className="w-4 h-4" />{t.label}
              </button>
            ))}
          </div>

          {/* Search & Export */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }}
                placeholder="Search by description, reference, type..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-300"
                data-testid="tx-search" />
            </div>
            <Button variant="outline" size="sm" className="gap-2 h-9 shrink-0" onClick={handleExport} disabled={exporting || !data?.total}
              data-testid="tx-export-btn">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </Button>
          </div>

          {/* Transactions Table */}
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-pink-400" /></div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-500">Date</th>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-500">Description</th>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-500 hidden md:table-cell">Type</th>
                    <th className="text-left px-3 py-2.5 font-medium text-gray-500 hidden lg:table-cell">Reference</th>
                    <th className="text-right px-3 py-2.5 font-medium text-gray-500">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.length > 0 ? data.transactions.map(tx => (
                    <tr key={tx.id} className="border-t hover:bg-gray-50" data-testid={`tx-row-${tx.id}`}>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                        {new Date(tx.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
                      </td>
                      <td className="px-3 py-2">
                        <p className="text-gray-900 font-medium truncate max-w-xs">{tx.description || tx.category || '—'}</p>
                      </td>
                      <td className="px-3 py-2 hidden md:table-cell">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">{tx.type?.replace(/_/g, ' ')}</span>
                      </td>
                      <td className="px-3 py-2 hidden lg:table-cell text-gray-400 font-mono truncate max-w-[200px]">{tx.reference}</td>
                      <td className={`px-3 py-2 text-right font-bold whitespace-nowrap ${tx.direction === 'debit' ? 'text-pink-600' : 'text-emerald-600'}`}>
                        {tx.direction === 'debit' ? '-' : '+'}{fmt(tx.amount)}
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-400">No transactions found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Showing {data.total > 0 ? page * pageSize + 1 : 0}-{Math.min((page + 1) * pageSize, data.total)} of {data.total}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft className="w-3 h-3" />
              </Button>
              <span className="px-2 py-1">{page + 1}/{totalPages || 1}</span>
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                <ChevronRight className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </>
      )}

      {selectedCompanyId && !data && !loading && (
        <div className="text-center py-16 text-gray-400">
          <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Loading transactions...</p>
        </div>
      )}

      {!selectedCompanyId && (
        <div className="text-center py-16 text-gray-400">
          <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Select a company above to view transactions</p>
        </div>
      )}
    </div>
  );
}
